import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { PopoverMenuItem, Text, ConfirmationModal, formatDate, Chip } from '@frontend/design-system';
import { StatusBadge } from '../../../../../../../components/status-badge';
import { useScheduleSettingsSmallMode } from '../../../../../../../hooks/settings/use-schedule-settings-small-mode';
import { safeParseJson } from '../../../../../../../utils';
import { useScheduleRequestItemHeaderController } from '../../hooks/use-schedule-request-item-header-controller';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';
import { ScheduleRequestItemHeaderUI } from '../components/schedule-request-item-header-ui';
import { ScheduleRequestDeleteConfirmationModal } from '../components/ScheduleRequestDeleteConfirmationModal';

type Props = {
  requestVersion: string;
  name: string;
  personId?: string;
  appointmentTime: string;
  personPhone: string;
  locationId: string;
  bookingDetails?: string;
  scheduleRequestId: string;
  isWidgetListItem?: boolean;
  updateSelectedScheduleRequest: () => void;
  markNotificationsAsRead: (val: string) => void;
  locationChip?: (locationName: string) => React.ReactNode;
};

export const PreferenceBasedScheduleRequestItemHeader = ({
  requestVersion,
  name,
  personId,
  appointmentTime,
  personPhone,
  locationId,
  scheduleRequestId,
  bookingDetails,
  isWidgetListItem,
  updateSelectedScheduleRequest,
  markNotificationsAsRead,
  locationChip,
}: Props) => {
  const { t } = useTranslation('schedule-requests');
  const { isSmallMode } = useScheduleSettingsSmallMode();
  const parsedJson = safeParseJson<any>(bookingDetails, null);
  const bookingCaptureSource = parsedJson?.sourceDetails?.source;
  const hasValidBookingCaptureSource = bookingCaptureSource === 'VOICE_AI' || bookingCaptureSource === 'SMS_AI';

  const {
    locationName,
    canSendMessages,
    appointmentTimeLabel,
    isDeleting,
    isUpdating,
    deleteScheduleRequestModalProps,
    rejectScheduleRequestModalControl,
    getMenuProps,
    getTriggerProps,
    handleDeleteScheduleRequest,
    handleDismissScheduleRequest,
    handleReviewRequestMenuItemClick,
    handleDeleteMenuItemClick,
    handleDismissMenuItemClick,
    handleDoneMenuItemClick,
    handleMessageMenuItemClick,
    handleCallMenuItemClick,
  } = useScheduleRequestItemHeaderController({
    requestVersion,
    locationId,
    scheduleRequestId,
    appointmentTime,
    isWidgetListItem,
    markNotificationsAsRead,
  });

  // TODO: Replace with real values from API
  const isPatientContacted = true;
  const lastContactedTime = new Date().toISOString();

  const primaryMeta = (
    <>
      <StatusBadge variant='ai-requested' iconOnly={isSmallMode} />
      {hasValidBookingCaptureSource && (
        <Chip variant='success' css={{ padding: theme.spacing(0.5), flexShrink: 0 }}>
          {bookingCaptureSource === 'VOICE_AI' && <Icon name='phone' size={12} />}
          {bookingCaptureSource === 'SMS_AI' && <Icon name='message' size={12} />}
        </Chip>
      )}
    </>
  );

  const secondaryMeta = (
    <>
      <Text textAlign='left' size='small'>
        {appointmentTimeLabel}
      </Text>
      {isPatientContacted ? (
        <Text textAlign='left' size='small' color={'subdued'}>
          {t('Last Contacted on {{datetime}}', {
            datetime: formatDate(lastContactedTime, 'MMM D, YYYY, h:mm A'),
          })}
        </Text>
      ) : undefined}
    </>
  );

  const menuItems = (
    <>
      {isWidgetListItem && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemReviewRequestBtn}
          Icon={() => <Icon name='lists-small' />}
          onClick={() => handleReviewRequestMenuItemClick(updateSelectedScheduleRequest)}
        >
          {t('Review Request')}
        </PopoverMenuItem>
      )}
      {personPhone && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemMessageBtn}
          Icon={() => <Icon name='message-small' />}
          css={{ width: '100%' }}
          disabled={!canSendMessages}
          hoverLabel={canSendMessages ? undefined : t('You do not have permission to send text messages')}
          onClick={async () => {
            await handleMessageMenuItemClick({
              personId,
              personPhone,
              threadGroupId: locationId,
              groupIds: [locationId],
            });
          }}
        >
          {t('Message')}
        </PopoverMenuItem>
      )}
      {personPhone && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemCallBtn}
          Icon={() => <Icon name='phone-small' />}
          css={{ width: '100%' }}
          onClick={handleCallMenuItemClick}
        >
          {t('Call')}
        </PopoverMenuItem>
      )}
      {!isPatientContacted && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemDismissBtn}
          Icon={() => <Icon name='x-small' />}
          onClick={handleDismissMenuItemClick}
        >
          {t('Dismiss')}
        </PopoverMenuItem>
      )}
      {isPatientContacted && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemDoneBtn}
          Icon={() => <Icon name='check-small' />}
          onClick={handleDoneMenuItemClick}
        >
          {t('Done')}
        </PopoverMenuItem>
      )}
      <PopoverMenuItem
        trackingId={ScheduleRequestsTrackingIds.requestListItemDeleteBtn}
        Icon={() => <Icon name='trash-small' />}
        onClick={handleDeleteMenuItemClick}
      >
        {t('Delete')}
      </PopoverMenuItem>
    </>
  );

  const modals = (
    <>
      <ScheduleRequestDeleteConfirmationModal
        onConfirm={handleDeleteScheduleRequest}
        modalProps={deleteScheduleRequestModalProps.modalProps}
      />
      <ConfirmationModal
        destructive
        {...rejectScheduleRequestModalControl.modalProps}
        title={t('Confirm Dismiss')}
        message={t('Are you sure you want to dismiss this request?')}
        onConfirm={handleDismissScheduleRequest}
        cancelTrackingId={ScheduleRequestsTrackingIds.dismissConfirmationModalCancelBtn}
        confirmTrackingId={ScheduleRequestsTrackingIds.dismissConfirmationModalConfirmBtn}
      />
    </>
  );

  return (
    <ScheduleRequestItemHeaderUI
      isLoading={isDeleting || isUpdating}
      locationName={locationName}
      name={name}
      primaryMeta={primaryMeta}
      secondaryMeta={secondaryMeta}
      menuItems={menuItems}
      modals={modals}
      locationChip={locationChip}
      getTriggerProps={getTriggerProps}
      getMenuProps={getMenuProps}
    />
  );
};
